import { createInterfaceIcon } from './interface-icon.js';
import { createAgentLogo } from './agent-logo.js';
import { agentAssistants } from '../config/agent-onboarding.js';
import { isControlActivation } from '../core/navigation.js';
import { positionPreview } from '../core/preview-position.js';
import { lockScroll } from '../core/scroll-lock.js';
import { agentPromptVersion, agentFeedbackDuration } from '../core/agent-onboarding.js';

const logos = import.meta.glob('../assets/agents/*.svg', { query: '?raw', import: 'default', eager: true });

/** Anchored popup on desktop/tablet, native modal bottom sheet on mobile. */
export function createAgentOnboarding(host, { onAction, onTrack, onError, onOpenChange, restoreFocus, isMobile }) {
  const document = host.ownerDocument;
  const window = document.defaultView;
  const root = host.shadowRoot;
  const node = (tag, name) => {
    const element = document.createElement(tag);
    element.className = `positive-nav-agent${name ? `-${name}` : ''}`;
    return element;
  };
  const button = (name, action) => {
    const element = node('button', name);
    element.classList.add('positive-nav-button');
    element.type = 'button';
    element.dataset.action = action;
    element.dataset.source = 'agentOnboarding';
    return element;
  };
  const element = node('div');
  element.hidden = true;
  const trigger = button('trigger', 'toggleAgentOnboarding');
  const mark = node('span', 'mark');
  mark.setAttribute('aria-hidden', 'true');
  const triggerProviders = new Map();
  for (const { id, logoIds = [id] } of agentAssistants) {
    const provider = node('span', 'provider');
    provider.dataset.agent = id;
    const logo = createAgentLogo(logos[`../assets/agents/${logoIds[0]}.svg`], document);
    if (logo) provider.append(logo);
    mark.append(provider);
    triggerProviders.set(id, provider);
  }
  const triggerLabel = node('span', 'trigger-label');
  trigger.append(mark, triggerLabel);
  element.append(trigger);

  const popup = node('section', 'popup');
  popup.id = `${host.instanceId}-agent-popup`;
  popup.hidden = true;
  popup.setAttribute('role', 'dialog');
  const sheet = node('dialog', 'sheet');
  sheet.id = `${popup.id}-sheet`;
  sheet.setAttribute('aria-modal', 'true');
  const handle = node('div', 'handle');
  handle.setAttribute('aria-hidden', 'true');
  trigger.setAttribute('aria-haspopup', 'dialog');
  trigger.setAttribute('aria-controls', popup.id);
  trigger.setAttribute('aria-expanded', 'false');
  const header = node('div', 'header');
  const title = node('h2', 'title');
  title.id = `${popup.id}-title`;
  const closeButton = button('close', 'closeAgentOnboarding');
  closeButton.append(createInterfaceIcon('times', document));
  header.append(title, closeButton);
  const description = node('p', 'description');
  description.id = `${popup.id}-description`;
  popup.setAttribute('aria-labelledby', title.id);
  popup.setAttribute('aria-describedby', description.id);
  sheet.setAttribute('aria-labelledby', title.id);
  sheet.setAttribute('aria-describedby', description.id);

  const connectionSection = node('section', 'connections');
  const connectionTitle = node('h3', 'section-title');
  connectionTitle.id = `${popup.id}-connect`;
  connectionSection.setAttribute('aria-labelledby', connectionTitle.id);
  const connectionList = node('ul', 'connection-list');
  connectionList.setAttribute('role', 'list');
  function connection({ id: provider, name, logoIds = [provider] }, ...controls) {
    const row = node('li', 'connection');
    row.dataset.provider = provider;
    const icon = node('span', logoIds.length > 1 ? 'connection-icons' : 'connection-icon');
    icon.setAttribute('aria-hidden', 'true');
    for (const id of logoIds) {
      const tile = logoIds.length > 1 ? node('span', 'connection-icon') : icon;
      const logo = createAgentLogo(logos[`../assets/agents/${id}.svg`], document);
      if (logo) tile.append(logo);
      if (tile !== icon) icon.append(tile);
    }
    const info = node('div', 'connection-info');
    const label = node('strong', 'connection-name');
    label.textContent = name;
    const description = node('span', 'connection-description');
    info.append(label, description);
    row.append(icon, info, ...controls);
    return { row, label, description };
  }
  connectionSection.append(connectionTitle, connectionList);
  const assistants = new Map();
  for (const metadata of agentAssistants) {
    const { id: provider, name } = metadata;
    const link = node('a', 'connect');
    link.classList.add('positive-nav-action', 'positive-nav-action-primary');
    link.dataset.action = 'connectAgent';
    link.dataset.source = 'agentOnboarding';
    link.dataset.provider = provider;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    const linkLabels = node('span', 'labels');
    linkLabels.setAttribute('aria-hidden', 'true');
    const linkLabel = node('span', 'label');
    linkLabel.dataset.state = 'connect';
    const linkSizingLabel = node('span', 'label');
    linkSizingLabel.dataset.state = 'sizer';
    linkLabels.append(linkLabel, linkSizingLabel);
    link.append(linkLabels);
    const control = button('copy', 'copyAgentPrompt');
    control.classList.remove('positive-nav-button');
    control.classList.add('positive-nav-action', 'positive-nav-action-primary');
    control.dataset.provider = provider;
    const labels = node('span', 'labels');
    labels.setAttribute('aria-hidden', 'true');
    const feedbackLabels = {};
    for (const state of ['idle', 'copied', 'error']) {
      feedbackLabels[state] = node('span', 'label');
      feedbackLabels[state].dataset.state = state;
      labels.append(feedbackLabels[state]);
    }
    const sizingLabel = node('span', 'label');
    sizingLabel.dataset.state = 'sizer';
    labels.append(sizingLabel);
    control.append(labels);
    const status = node('span', 'status');
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.setAttribute('aria-atomic', 'true');
    const entry = { ...connection(metadata, link, control), metadata, provider, name, control, link, linkLabel, linkSizingLabel,
      feedbackLabels, sizingLabel, status, feedback: 'idle', pending: false, timer: undefined, operation: 0 };
    entry.row.append(status);
    assistants.set(provider, entry);
    connectionList.append(entry.row);
  }
  const connectionHint = node('p', 'hint');
  connectionSection.append(connectionHint);

  const permissions = node('p', 'permissions');
  permissions.append(createInterfaceIcon('shield-check', document));
  const permissionText = node('span', 'permission-text');
  permissions.append(permissionText);
  const body = node('div', 'body');
  body.append(description, connectionSection, permissions);
  popup.append(handle, header, body);

  let config;
  let t;
  let locale;
  let observer;
  let frame;
  let connected = false;
  let disposed = false;
  let open = false;
  let layout;
  let switchingLayout = false;
  let releaseScroll;
  let backdropDown = false;
  let updateRevision = 0;
  const impressions = new Set();
  const identity = () => config?.url ?? Object.values(config?.assistants ?? {})[0]?.url;
  const track = (event, context = config, extra = {}) => {
    if (disposed || !context) return;
    onTrack({ event: `agent_onboarding_${event}`, source: 'agentOnboarding', productId: context.productId,
      locale, promptVersion: agentPromptVersion, ...extra });
  };
  const renderFeedback = (entry) => {
    entry.control.dataset.state = entry.feedback;
    const label = entry.feedback === 'copied' ? t('agent.copyDone')
      : entry.feedback === 'error' ? t('agent.copyError') : t('agent.copy', { product: config?.name });
    entry.control.setAttribute('aria-label', `${entry.name} · ${label}`);
    entry.control.title = `${entry.name} · ${label}`;
    entry.status.textContent = entry.feedback === 'copied' ? `${entry.name} · ${t('agent.copied')}`
      : entry.feedback === 'error' ? `${entry.name} · ${t('agent.copyError')}` : '';
  };
  const resetFeedback = (target) => {
    for (const entry of target ? [target] : assistants.values()) {
      window.clearTimeout(entry.timer);
      entry.timer = undefined;
      entry.feedback = 'idle';
      entry.pending = false;
      entry.operation++;
      entry.control.removeAttribute('aria-disabled');
      if (t) renderFeedback(entry);
    }
  };
  const viewport = () => ({
    left: window.visualViewport?.offsetLeft ?? 0, top: window.visualViewport?.offsetTop ?? 0,
    width: window.visualViewport?.width ?? document.documentElement.clientWidth,
    height: window.visualViewport?.height ?? document.documentElement.clientHeight,
  });
  function reposition() {
    if (!open || disposed) return;
    if (layout === 'sheet') return;
    const rect = trigger.getBoundingClientRect();
    const bar = root.querySelector('.positive-nav-bar').getBoundingClientRect();
    const view = viewport();
    if (!host.isConnected || !rect.width || !rect.height || bar.bottom <= view.top
      || bar.top >= view.top + view.height || rect.right <= view.left || rect.left >= view.left + view.width) {
      close({ reason: 'hidden' });
      return;
    }
    popup.style.maxWidth = `${Math.max(0, view.width - 24)}px`;
    popup.style.maxHeight = `${Math.max(0, view.height - 24)}px`;
    const size = popup.getBoundingClientRect();
    const position = positionPreview({ left: rect.right - size.width, top: bar.top, bottom: bar.bottom },
      size, view, { gap: 8, margin: 12 });
    const origin = popup.dataset.positioning === 'viewport' ? { left: 0, top: 0 } : host.getBoundingClientRect();
    popup.style.left = `${position.left - origin.left}px`;
    popup.style.top = `${position.top - origin.top}px`;
    popup.style.maxHeight = `${position.maxHeight}px`;
  }
  function schedulePosition(event) {
    if (!open || (event?.target?.nodeType && popup.contains(event.target)) || frame !== undefined) return;
    frame = window.requestAnimationFrame(() => { frame = undefined; refreshLayout(); });
  }
  const controls = () => [closeButton, ...[...assistants.values()].flatMap(({ link, control }) => [link, control])]
    .filter((control) => !control.disabled && !control.closest('[hidden]'));
  function outside(event) {
    if (layout === 'sheet') return;
    const path = event.composedPath();
    if (!path.includes(popup) && !path.includes(trigger)) close({ reason: 'outside' });
  }
  function keyboard(event) {
    if (!open || event.defaultPrevented) return;
    if (event.key === 'Escape') {
      onAction(action('closeAgentOnboarding', 'escape'), event);
      event.preventDefault();
    } else if (event.key === 'Tab' && !event.altKey && !event.ctrlKey && !event.metaKey) {
      const active = root.activeElement;
      const items = controls();
      if (layout === 'sheet') {
        if (event.shiftKey ? active === items[0] : active === items[items.length - 1]) {
          event.preventDefault();
          (event.shiftKey ? items[items.length - 1] : items[0])?.focus({ preventScroll: true });
        }
        return;
      }
      if (active === items[0] && event.shiftKey) {
        event.preventDefault(); close({ reason: 'tab', restoreFocus: true });
      } else if (active === items[items.length - 1] && !event.shiftKey) {
        const barControls = [...root.querySelector('.positive-nav-bar').querySelectorAll('button, a[href]')]
          .filter((control) => control.getClientRects().length);
        const next = barControls[barControls.indexOf(trigger) + 1];
        if (next) { event.preventDefault(); close({ reason: 'tab' }); next.focus({ preventScroll: true }); }
      }
    }
  }
  function visibility() {
    if (document.hidden) close({ reason: 'hidden' });
    else recordImpression();
  }
  function listenPopup(enabled) {
    const method = enabled ? 'addEventListener' : 'removeEventListener';
    document[method]('pointerdown', outside, true);
    document[method]('keydown', keyboard);
    root.querySelector('.positive-nav-bar')[method]('scroll', schedulePosition);
    window[method]('scroll', schedulePosition, true);
    window[method]('resize', schedulePosition);
    window.visualViewport?.[method]('resize', schedulePosition);
    window.visualViewport?.[method]('scroll', schedulePosition);
  }
  function dismissPresentation() {
    backdropDown = false;
    if (popup.hasAttribute('popover') && popup.matches(':popover-open')) popup.hidePopover();
    if (sheet.open) sheet.close();
    releaseScroll?.();
    releaseScroll = undefined;
    popup.removeAttribute('popover');
  }
  function present() {
    layout = isMobile() ? 'sheet' : 'popup';
    popup.dataset.layout = layout;
    trigger.setAttribute('aria-controls', layout === 'sheet' ? sheet.id : popup.id);
    for (const property of ['left', 'top', 'max-width', 'max-height']) popup.style.removeProperty(property);
    if (layout === 'sheet') {
      popup.setAttribute('role', 'document');
      popup.removeAttribute('aria-labelledby');
      popup.removeAttribute('aria-describedby');
      popup.dataset.positioning = 'sheet';
      sheet.append(popup);
      sheet.showModal();
      releaseScroll = lockScroll(document);
    } else {
      popup.setAttribute('role', 'dialog');
      popup.setAttribute('aria-labelledby', title.id);
      popup.setAttribute('aria-describedby', description.id);
      if (popup.parentNode !== root) root.append(popup);
      if (typeof popup.showPopover === 'function') {
        popup.setAttribute('popover', 'manual');
        popup.dataset.positioning = 'viewport';
        popup.showPopover();
      } else {
        popup.dataset.positioning = 'host';
      }
    }
  }
  function refreshLayout(focused = root.activeElement) {
    if (disposed) return;
    trigger.setAttribute('aria-controls', isMobile() ? sheet.id : popup.id);
    if (!open) return;
    if (layout !== (isMobile() ? 'sheet' : 'popup')) {
      const scroll = layout === 'sheet' ? body.scrollTop : popup.scrollTop;
      const previousFocus = popup.contains(focused) ? focused : null;
      switchingLayout = true;
      try {
        dismissPresentation();
        present();
        (layout === 'sheet' ? body : popup).scrollTop = scroll;
        if (previousFocus?.isConnected && !previousFocus.disabled) previousFocus.focus({ preventScroll: true });
      } catch (error) {
        close({ reason: 'error', restoreFocus: true });
        onError(error);
      } finally { switchingLayout = false; }
    }
    reposition();
  }
  function close({ reason = 'button', restoreFocus: returnFocus = false } = {}) {
    if (!open) return;
    open = false;
    resetFeedback();
    window.cancelAnimationFrame(frame);
    frame = undefined;
    trigger.setAttribute('aria-expanded', 'false');
    dismissPresentation();
    popup.hidden = true;
    listenPopup(false);
    onOpenChange(false);
    track('closed', config, { reason });
    if (returnFocus && !open && host.isConnected && !element.hidden) trigger.focus({ preventScroll: true });
  }
  function show() {
    if (disposed || !connected || element.hidden || open) return;
    open = true;
    popup.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    onOpenChange(true);
    if (disposed || !open) return;
    try { present(); } catch (error) {
      close({ reason: 'error', restoreFocus: true });
      throw error;
    }
    body.scrollTop = 0;
    popup.scrollTop = 0;
    reposition();
    if (!open) return;
    listenPopup(true);
    controls().find((control) => control !== closeButton)?.focus({ preventScroll: true });
    track('opened');
  }
  function recordImpression() {
    if (!connected || disposed || element.hidden || !config || document.hidden) return;
    const rect = element.getBoundingClientRect();
    if (!rect.width || !rect.height || rect.right <= 0 || rect.bottom <= 0
      || rect.left >= window.innerWidth || rect.top >= window.innerHeight) return;
    const key = `${config.productId}:${identity()}`;
    if (impressions.has(key)) return;
    impressions.add(key);
    track('shown');
  }
  const stopObserving = () => { observer?.disconnect(); observer = undefined; };
  function observe() {
    stopObserving();
    if (!connected || element.hidden || disposed) return;
    if (typeof window.IntersectionObserver === 'function') {
      observer = new window.IntersectionObserver((entries) => {
        if (entries.some(({ isIntersecting }) => isIntersecting)) recordImpression();
      });
      observer.observe(element);
    }
    window.queueMicrotask(() => { if (!disposed) recordImpression(); });
  }
  async function copyPrompt(entry) {
    const context = config;
    const operation = ++entry.operation;
    window.clearTimeout(entry.timer);
    entry.pending = true;
    entry.control.setAttribute('aria-disabled', 'true');
    const active = () => !disposed && connected && open && config === context
      && operation === entry.operation;
    try {
      if (typeof window.navigator.clipboard?.writeText !== 'function') {
        throw new window.DOMException('Clipboard access is unavailable.', 'NotSupportedError');
      }
      await window.navigator.clipboard.writeText(context.prompt);
      if (!active()) return;
      entry.pending = false;
      entry.control.removeAttribute('aria-disabled');
      entry.feedback = 'copied';
      renderFeedback(entry);
      entry.timer = window.setTimeout(() => resetFeedback(entry), agentFeedbackDuration);
      track('copied', context, { provider: entry.provider });
    } catch (error) {
      if (!active()) return;
      entry.pending = false;
      entry.control.removeAttribute('aria-disabled');
      entry.feedback = 'error';
      renderFeedback(entry);
      track('copy_failed', context, { provider: entry.provider,
        reason: error?.name === 'NotSupportedError' ? 'unavailable' : 'failed' });
      onError(error);
    }
  }
  function action(name, reason, provider) {
    if (!config || element.hidden || disposed) return null;
    if (name === 'toggleAgentOnboarding') name = open ? 'closeAgentOnboarding' : 'openAgentOnboarding';
    const entry = assistants.get(provider);
    const assistant = config.assistants[provider];
    if (name === 'connectAgent' && (!open || !entry || assistant?.mode !== 'connect')) return null;
    if (name === 'copyAgentPrompt' && (!open || !entry || entry.pending || assistant?.mode !== 'copy' || !config.prompt)) return null;
    return { action: name, source: 'agentOnboarding', productId: config.productId,
      url: name === 'connectAgent' ? assistant.url : identity(),
      ...(name === 'connectAgent' ? { provider, target: '_blank' } : {}),
      ...(name === 'copyAgentPrompt' ? { provider } : {}), ...(reason ? { reason } : {}) };
  }
  const activateControl = (event) => {
    const control = event.target.closest?.('[data-action]');
    if (!control || !popup.contains(control) || event.defaultPrevented || !isControlActivation(event, control)) return;
    const intent = action(control.dataset.action, control === closeButton ? 'button' : undefined, control.dataset.provider);
    if (intent) onAction(intent, event);
  };
  popup.addEventListener('click', activateControl);
  popup.addEventListener('auxclick', activateControl);
  const isBackdrop = (event) => {
    const rect = sheet.getBoundingClientRect();
    return event.target === sheet && (event.clientX < rect.left || event.clientX > rect.right
      || event.clientY < rect.top || event.clientY > rect.bottom);
  };
  const sheetPointerDown = (event) => { backdropDown = isBackdrop(event); };
  const sheetClick = (event) => {
    if (open && !event.defaultPrevented && event.button === 0 && backdropDown && isBackdrop(event)) {
      const intent = action('closeAgentOnboarding', 'outside');
      if (intent) onAction(intent, event);
    }
    backdropDown = false;
  };
  const sheetCancel = (event) => {
    if (!open || event.defaultPrevented) return;
    onAction(action('closeAgentOnboarding', 'escape'), event);
    event.preventDefault();
  };
  const sheetClose = () => {
    if (open && layout === 'sheet' && !switchingLayout && !sheet.open) close({ reason: 'native', restoreFocus: true });
  };
  sheet.addEventListener('pointerdown', sheetPointerDown);
  sheet.addEventListener('click', sheetClick);
  sheet.addEventListener('cancel', sheetCancel);
  sheet.addEventListener('close', sheetClose);

  return {
    element, popup, sheet, action, close, refreshLayout,
    get isOpen() { return open; },
    activate(intent, event) {
      const entry = assistants.get(intent.provider);
      const assistant = config?.assistants[intent.provider];
      if (disposed || !connected || element.hidden || !config || intent.productId !== config.productId
        || intent.url !== (intent.action === 'connectAgent' ? assistant?.url : identity())) return false;
      if (intent.action === 'openAgentOnboarding') show();
      else if (intent.action === 'closeAgentOnboarding') close({ reason: intent.reason ?? 'button', restoreFocus: true });
      else if (intent.action === 'copyAgentPrompt') {
        if (!open || !entry || entry.pending || assistant?.mode !== 'copy' || !config.prompt
          || !event.composedPath().includes(entry.control)) return false;
        void copyPrompt(entry);
      } else if (intent.action === 'connectAgent') {
        const link = entry?.link;
        if (!open || assistant?.mode !== 'connect' || !link?.isConnected || link.href !== assistant.url
          || !event.composedPath().includes(link)) return false;
        const previous = config;
        track('connect_clicked', config, { provider: intent.provider });
        return !disposed && connected && open && config === previous && link.isConnected && link.href === assistant.url;
      }
      return true;
    },
    update(options, next, translate) {
      const revision = ++updateRevision;
      if (config?.productId !== next?.productId || config?.url !== next?.url || config?.prompt !== next?.prompt
        || agentAssistants.some(({ id }) => config?.assistants[id]?.mode !== next?.assistants[id]?.mode
          || config?.assistants[id]?.url !== next?.assistants[id]?.url)) {
        const focused = popup.contains(root.activeElement) || root.activeElement === trigger;
        close({ reason: 'product' });
        // Closing can synchronously trigger another update through onTrack.
        if (disposed || revision !== updateRevision) return;
        config = next;
        resetFeedback();
        if (!config && focused && connected) restoreFocus();
      }
      if (disposed || revision !== updateRevision) return;
      t = translate;
      locale = options.locale;
      element.hidden = !config;
      host.toggleAttribute('data-agent-onboarding', !!config);
      const availableProviders = Object.keys(config?.assistants ?? {}).slice(0, 2);
      for (const [provider, icon] of triggerProviders) icon.hidden = !availableProviders.includes(provider);
      triggerLabel.textContent = t('agent.useAssistant');
      trigger.setAttribute('aria-label', t('agent.useAssistant'));
      trigger.title = t('agent.useAssistant');
      title.textContent = t('agent.title', { product: config?.name ?? '' });
      description.textContent = t('agent.description', { product: config?.name ?? '' });
      closeButton.setAttribute('aria-label', t('actions.close'));
      closeButton.title = t('actions.close');
      const available = Object.values(config?.assistants ?? {});
      const hasConnection = available.some(({ mode }) => mode === 'connect');
      const hasCopy = available.some(({ mode }) => mode === 'copy');
      for (const entry of assistants.values()) {
        const assistant = config?.assistants[entry.provider];
        entry.name = entry.metadata.nameKey ? t(entry.metadata.nameKey) : entry.metadata.name;
        entry.label.textContent = entry.name;
        entry.feedbackLabels.idle.textContent = t('agent.copyPrompt');
        entry.feedbackLabels.copied.textContent = t('agent.copyDone');
        entry.feedbackLabels.error.textContent = t('agent.copyRetry');
        entry.sizingLabel.textContent = hasConnection ? t('agent.connect') : '';
        entry.control.hidden = assistant?.mode !== 'copy';
        entry.control.disabled = assistant?.mode !== 'copy';
        entry.link.hidden = assistant?.mode !== 'connect';
        entry.linkLabel.textContent = t('agent.connect');
        entry.linkSizingLabel.textContent = hasCopy ? t('agent.copyPrompt') : '';
        if (assistant?.mode === 'connect') entry.link.href = assistant.url;
        else entry.link.removeAttribute('href');
        entry.link.setAttribute('aria-label', `${entry.name} · ${t('agent.connect')} · ${t('accessibility.newTab')}`);
        entry.row.hidden = !assistant;
        entry.description.textContent = entry.metadata.examples ?? t('agent.availableFor', { product: config?.name ?? '' });
        renderFeedback(entry);
      }
      connectionTitle.textContent = t('agent.connectTitle');
      connectionHint.textContent = t('agent.connectionHint');
      permissionText.textContent = t('agent.permissions');
      refreshLayout();
      observe();
    },
    connect() {
      if (disposed || connected) return;
      connected = true;
      refreshLayout();
      document.addEventListener('visibilitychange', visibility);
      observe();
    },
    disconnect() {
      connected = false;
      close({ reason: 'disconnect' });
      resetFeedback();
      stopObserving();
      document.removeEventListener('visibilitychange', visibility);
    },
    dispose() {
      this.disconnect();
      disposed = true;
      popup.removeEventListener('click', activateControl);
      popup.removeEventListener('auxclick', activateControl);
      sheet.removeEventListener('pointerdown', sheetPointerDown);
      sheet.removeEventListener('click', sheetClick);
      sheet.removeEventListener('cancel', sheetCancel);
      sheet.removeEventListener('close', sheetClose);
      popup.remove();
      sheet.remove();
      for (const { link } of assistants.values()) link.removeAttribute('href');
      config = undefined;
      impressions.clear();
    },
  };
}
